<template>
  <div class="et-root">

    <!-- HEADER -->
    <div class="et-header">
      <div class="et-header__left">
        <div class="et-icon-wrap">
          <v-icon size="14" color="#ef4444">mdi-chart-areaspline</v-icon>
        </div>
        <div class="et-header__meta">
          <span class="et-title">Error Trends</span>
          <span class="et-subtitle">Quality overview by period</span>
        </div>
      </div>
      <div class="et-filters">
        <!-- Quarter filter with calendar icon -->
        <div class="et-select-wrap">
          <v-icon size="13" color="#ef4444" class="et-cal-icon">mdi-calendar-month</v-icon>
          <select class="et-select" v-model="selectedQuarter" aria-label="Filter quarter">
            <option v-for="q in quarterOptions" :key="q.value" :value="q.value">{{ q.label }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- LOADER -->
    <div class="et-loader" v-if="loading">
      <div class="et-shimmer-wrap">
        <div class="et-shimmer et-shimmer--header"></div>
        <div class="et-shimmer et-shimmer--bar et-shimmer--w80"></div>
        <div class="et-shimmer et-shimmer--bar et-shimmer--w60"></div>
        <div class="et-shimmer et-shimmer--bar et-shimmer--w70"></div>
      </div>
    </div>

    <!-- WAVE CHART -->
    <div class="et-chart-wrap" v-else-if="chartTrends.length">
      <canvas ref="chartCanvas"></canvas>
    </div>

    <!-- EMPTY -->
    <div class="et-empty" v-else>
      <div class="et-empty__icon">
        <v-icon size="26" color="#fca5a5">mdi-chart-areaspline</v-icon>
      </div>
      <span class="et-empty__title">No trend data</span>
      <span class="et-empty__sub">Data will appear once tasks are reviewed</span>
    </div>

  </div>
</template>

<script>
import { Chart, LineController, LineElement, PointElement, Filler, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import TimeSheetService from "@/api/service/TimeSheet/TimeSheetService";

Chart.register(LineController, LineElement, PointElement, Filler, CategoryScale, LinearScale, Tooltip, Legend);

export default {
  props: ["userId"],
  data() {
    const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
    return {
      errorTrends: [],
      loading: false,
      chartInstance: null,
      selectedQuarter: currentQuarter,
      quarterOptions: [
        { value: 1, label: "Q1 Jan-Mar" },
        { value: 2, label: "Q2 Apr-Jun" },
        { value: 3, label: "Q3 Jul-Sep" },
        { value: 4, label: "Q4 Oct-Dec" },
      ],
    };
  },
  computed: {
    chartTrends() {
      const all = this.errorTrends;
      if (!all.length) return [];

      // Quarter period_type labels as returned by the API
      const quarterPeriodMap = {
        1: "JAN-MAR",
        2: "APR-JUN",
        3: "JUL-SEP",
        4: "OCT-DEC",
      };

      // Helper to find an entry by period_type (case-insensitive)
      const find = (label) =>
        all.find(d => d.period_type && d.period_type.toUpperCase().includes(label.toUpperCase()));

      // Fixed rows: This Week and This Month are always shown
      const week  = find("THIS WEEK");
      const month = find("THIS MONTH");
      const year  = find("THIS YEAR");

      // Derive the selected quarter entry
      let quarterEntry = null;

      if (this.selectedQuarter === 1) {
        // JAN-MAR is not returned by API — derive it from THIS YEAR minus other quarters
        const otherQuarters = [2, 3, 4].map(q => find(quarterPeriodMap[q]));
        if (year) {
          const sumField = (field) =>
            otherQuarters.reduce((acc, d) => acc + (d ? (d[field] || 0) : 0), 0);
          quarterEntry = {
            ...year,
            period_type: "JAN-MAR",
            total_issues: Math.max(0, (year.total_issues || 0) - sumField("total_issues")),
            pass:         Math.max(0, (year.pass        || 0) - sumField("pass")),
            sample:       Math.max(0, (year.sample      || 0) - sumField("sample")),
          };
        }
      } else {
        quarterEntry = find(quarterPeriodMap[this.selectedQuarter]);
      }

      // Build the display rows: week, month, quarter (in that order)
      const rows = [week, month, quarterEntry].filter(Boolean);
      return rows.map(item => ({ ...item, label: item.period_type }));
    },
  },
  watch: {
    userId(val) { if (val) this.getTrends(); },
    selectedQuarter() { this.$nextTick(() => this.buildChart()); },
    errorTrends() { this.$nextTick(() => this.buildChart()); },
  },
  created() { this.getTrends(); },
  beforeUnmount() { this.destroyChart(); },
  methods: {
    async getTrends() {
      this.loading = true;
      try {
        const res = await TimeSheetService.getErrorTrends({
          userId: this.userId,
        });
        this.errorTrends = res || [];
      } finally {
        this.loading = false;
      }
    },

    buildChart() {
      this.destroyChart();
      const canvas = this.$refs.chartCanvas;
      if (!canvas || !this.chartTrends.length) return;

      const ctx    = canvas.getContext("2d");
      const labels = this.chartTrends.map(d => d.label);

      const mkGrad = (r, g, b) => {
        const gr = ctx.createLinearGradient(0, 0, 0, 160);
        gr.addColorStop(0, `rgba(${r},${g},${b},0.28)`);
        gr.addColorStop(1, `rgba(${r},${g},${b},0.01)`);
        return gr;
      };

      this.chartInstance = new Chart(canvas, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "Total Tasks",
              data: this.chartTrends.map(d => d.total_issues),
              borderColor: "#6366f1",
              backgroundColor: mkGrad(99, 102, 241),
              borderWidth: 2.5, fill: true, tension: 0.45,
              pointBackgroundColor: "#6366f1", pointBorderColor: "#fff",
              pointBorderWidth: 2, pointRadius: 5, pointHoverRadius: 7,
            },
            {
              label: "Errors",
              data: this.chartTrends.map(d => d.sample),
              borderColor: "#ef4444",
              backgroundColor: mkGrad(239, 68, 68),
              borderWidth: 2.5, fill: true, tension: 0.45,
              pointBackgroundColor: "#ef4444", pointBorderColor: "#fff",
              pointBorderWidth: 2, pointRadius: 5, pointHoverRadius: 7,
            },
            {
              label: "Passed",
              data: this.chartTrends.map(d => d.pass),
              borderColor: "#22c55e",
              backgroundColor: mkGrad(34, 197, 94),
              borderWidth: 2.5, fill: true, tension: 0.45,
              pointBackgroundColor: "#22c55e", pointBorderColor: "#fff",
              pointBorderWidth: 2, pointRadius: 5, pointHoverRadius: 7,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                boxWidth: 10, boxHeight: 10,
                borderRadius: 3, useBorderRadius: true,
                font: { size: 10, family: "system-ui" },
                color: "#64748b", padding: 12,
              },
            },
            tooltip: {
              backgroundColor: "#1e293b",
              titleFont: { size: 11, weight: "700" },
              bodyFont: { size: 11 },
              padding: 10, cornerRadius: 8,
              callbacks: { label: c => ` ${c.dataset.label}: ${c.parsed.y}` },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 10, family: "system-ui" }, color: "#64748b", maxRotation: 0 },
              border: { display: false },
            },
            y: {
              beginAtZero: true,
              grid: { color: "#f1f5f9", lineWidth: 1 },
              ticks: { font: { size: 10, family: "system-ui" }, color: "#94a3b8", precision: 0 },
              border: { display: false },
            },
          },
        },
      });
    },

    destroyChart() {
      if (this.chartInstance) { this.chartInstance.destroy(); this.chartInstance = null; }
    },
  },
};
</script>

<style scoped>
.et-root {
  height: 100%;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 2px 12px rgba(239,68,68,0.06);
  display: flex;
  flex-direction: column;
  padding: 14px 16px 12px;
  gap: 10px;
  overflow: hidden;
}

.et-header { display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; }
.et-header__left { display: flex; align-items: center; gap: 9px; }
.et-filters { display: flex; align-items: center; gap: 6px; }
.et-select-wrap {
  display: flex; align-items: center; gap: 5px;
  border: 1px solid #fecaca; border-radius: 8px;
  background: #fff5f5; padding: 0 8px;
  height: 28px;
}
.et-select-wrap:focus-within { border-color: #ef4444; box-shadow: 0 0 0 2px rgba(239,68,68,0.10); }
.et-cal-icon { flex-shrink: 0; }
.et-select {
  height: 100%; border: none; background: transparent;
  color: #334155; font-size: 10px; font-weight: 700;
  outline: none; cursor: pointer; padding: 0;
  min-width: 90px;
}
.et-icon-wrap {
  width: 30px; height: 30px; border-radius: 9px;
  background: #fef2f2; border: 1px solid #fecaca;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.et-header__meta { display: flex; flex-direction: column; gap: 1px; }
.et-title    { font-size: 13px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px; }
.et-subtitle { font-size: 10px; color: #94a3b8; }

.et-chart-wrap { flex: 1; min-height: 0; position: relative; }
.et-chart-wrap canvas { width: 100% !important; height: 100% !important; }

.et-empty {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 6px;
}
.et-empty__icon {
  width: 52px; height: 52px; border-radius: 14px;
  background: #fef2f2; display: flex; align-items: center; justify-content: center;
  margin-bottom: 4px;
}
.et-empty__title { font-size: 12px; font-weight: 700; color: #334155; }
.et-empty__sub   { font-size: 10px; color: #94a3b8; }

.et-loader {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.et-shimmer-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.et-shimmer {
  border-radius: 8px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e8edf5 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: et-shimmer-move 1.4s ease-in-out infinite;
}
.et-shimmer--header { height: 36px; width: 100%; border-radius: 10px; }
.et-shimmer--bar    { height: 14px; }
.et-shimmer--w80    { width: 80%; }
.et-shimmer--w60    { width: 60%; }
.et-shimmer--w70    { width: 70%; }
@keyframes et-shimmer-move {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
